import { useState, useEffect } from 'react';

interface LocationData {
  ip: string;
  city: string;
  isp: string;
}

export function useLocationData() {
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLocationData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Список сервисов для попытки получения данных (по порядку приоритета)
        const apiServices = [
          // 1. ipwhois.app (бесплатный, без регистрации)
          async () => {
            const response = await fetch('https://ipwhois.app/json/');
            if (!response.ok) throw new Error('ipwhois failed');
            const data = (await response.json()) as {
              ip?: string;
              city?: string;
              isp?: string;
              org?: string;
            };
            return {
              ip: data.ip ?? 'Unknown',
              city: data.city ?? 'Unknown',
              isp: data.isp ?? data.org ?? 'Unknown',
            };
          },
          // 2. ip-api.com (бесплатно до 45 запросов/минуту)
          async () => {
            const response = await fetch('https://ip-api.com/json/?fields=query,city,isp');
            if (!response.ok) throw new Error('ip-api failed');
            const data = (await response.json()) as {
              query?: string;
              city?: string;
              isp?: string;
            };
            return {
              ip: data.query ?? 'Unknown',
              city: data.city ?? 'Unknown',
              isp: data.isp ?? 'Unknown',
            };
          },
          // 3. Простой API для получения IP
          async () => {
            const ipResponse = await fetch('https://api.ipify.org?format=json');
            if (!ipResponse.ok) throw new Error('ipify failed');
            const ipData = (await ipResponse.json()) as { ip?: string };
            return {
              ip: ipData.ip ?? 'Unknown',
              city: 'Unknown',
              isp: 'Unknown',
            };
          },
        ];

        // Пробуем каждый сервис по очереди
        for (const service of apiServices) {
          try {
            const data = await service();
            setLocationData(data);
            setError(null);
            return;
          }
          catch (err) {
            console.warn('Service failed, trying next:', err);
            continue;
          }
        }

        // Если все сервисы не сработали
        throw new Error('All services failed');
      }
      catch (err) {
        console.error('Error fetching location data:', err);
        setError('Failed to load location data');

        // Fallback данные в случае ошибки
        setLocationData({
          ip: 'Loading...',
          city: 'Loading...',
          isp: 'Loading...',
        });
      }
      finally {
        setLoading(false);
      }
    };

    void fetchLocationData();
  }, []);

  return {
    locationData,
    loading,
    error,
  };
}
